import { SelectionModel } from '@angular/cdk/collections';
import {
    AfterContentInit,
    Component,
    ContentChild,
    ContentChildren,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
} from '@angular/core';
import { IAumMenuItem } from '@modeso/aumico-lib-core-fe';
import { HamCellDirective, HamHeaderCellDirective, HamMenuButtonDirective } from '../../directives';

// New directive for subtable template
export class HamSubtableDirective {
    constructor(public templateRef: TemplateRef<unknown>) {}
}

@Component({
    selector: 'ham-table-with-actions',
    template: `
        <div class="table-scroll-container" [class.scroll-enabled]="!pagination" (scroll)="onScroll($event)">
            <table style="{{ tableStyles }}">
                <thead>
                    <ng-content select="[groupHeaderRow]"></ng-content>
                    <tr>
                        <!-- Expand/Collapse column -->
                        <th class="hamExpandCol" *ngIf="hasSubtable">
                            <span
                                hamIconShapes
                                class="icon expand-all-icon"
                                icon="assets/hamIcons/chevron-right.svg"
                                [class.rotated]="allExpanded"
                                [variant]="'transparent'"
                                [size]="'regular'"
                                (click)="toggleExpandAll()"
                            ></span>
                        </th>
                        <th class="hamCheckboxCol" *ngIf="!hideActions">
                            <span hamCheckbox [checked]="allRowsChecked" (change)="toggleCheckAll()"> </span>
                        </th>
                        <th *ngFor="let header of headerTemplates; let i = index" [class]="columnClasses[i] || ''">
                            <ng-container *ngTemplateOutlet="header"></ng-container>
                        </th>
                        <th class="hamMenuCol" *ngIf="!hideActions">
                            <span
                                hamIconShapes
                                class="icon"
                                icon="assets/hamIcons/dots-horizontal.svg"
                                [variant]="'transparent'"
                                [size]="'regular'"
                                (click)="toggleOptionsMenu($event)"
                            ></span>
                            <div
                                class="menu-flyout"
                                hamFlyout
                                *ngIf="isMenuOpenIndex"
                                (closeFlyout)="closeOptionsMenu()"
                                [position]="menuPosition"
                            >
                                <ul>
                                    <li
                                        *ngFor="let option of menuItems"
                                        (click)="onMenuOptionClicked(option)"
                                        [class.disabled]="
                                            option.disabled && this.checkboxSelection.selected.length == 0
                                        "
                                    >
                                        <span
                                            hamIconShapes
                                            icon="{{ option.icon }}"
                                            [variant]="'transparent'"
                                            [size]="'xs'"
                                        ></span>
                                        <span>{{ option.caption }}</span>
                                    </li>
                                </ul>
                            </div>
                        </th>
                        <th class="hamMenuCol" *ngIf="hideActions && showActionsInRowsOnly"></th>
                    </tr>
                </thead>
                <tbody>
                    <ng-container *ngFor="let row of paginatedData; let i = index">
                        <!-- Main row -->
                        <tr
                            [class.selected]="row === selectedRow"
                            [class.expanded]="isRowExpanded(row)"
                            [attr.data-testid]="rowTestId || null"
                            (click)="selectRow(row)"
                        >
                            <!-- Expand/Collapse button -->
                            <td class="hamExpandCol" *ngIf="hasSubtable">
                                <span
                                    hamIconShapes
                                    class="icon expand-icon"
                                    icon="assets/hamIcons/chevron-right.svg"
                                    [class.rotated]="isRowExpanded(row)"
                                    [variant]="'transparent'"
                                    [size]="'regular'"
                                    (click)="toggleSubtable(row, $event)"
                                ></span>
                            </td>
                            <td class="hamCheckboxCol" *ngIf="!hideActions">
                                <span
                                    class="custom-checkbox"
                                    hamCheckbox
                                    [checked]="checkboxSelection.isSelected(row)"
                                    (change)="rowChecked(row)"
                                    [disabled]="row.disableSelection"
                                >
                                </span>
                            </td>
                            <td
                                *ngFor="let cellDirective of cellDirectives; let i = index"
                                [class]="columnClasses[i] || ''"
                                (click)="onCellClicked(cellDirective, row, $event)"
                            >
                                <ng-container
                                    *ngTemplateOutlet="cellDirective.template; context: { $implicit: row }"
                                ></ng-container>
                            </td>
                            <td class="hamMenuCol" *ngIf="!hideActions">
                                <div style="display: flex">
                                    <ng-container *ngFor="let button of menuButtons">
                                        <ng-container
                                            *ngTemplateOutlet="button; context: { $implicit: row }"
                                        ></ng-container>
                                    </ng-container>
                                </div>
                            </td>
                            <td class="hamMenuCol" *ngIf="hideActions && showActionsInRowsOnly">
                                <div style="display: flex">
                                    <ng-container *ngFor="let button of menuButtons">
                                        <ng-container
                                            *ngTemplateOutlet="button; context: { $implicit: row }"
                                        ></ng-container>
                                    </ng-container>
                                </div>
                            </td>
                        </tr>

                        <!-- Subtable row -->
                        <tr *ngIf="isRowExpanded(row) && hasSubtable" class="subtable-row">
                            <td [colSpan]="getTotalColumnCount()" class="subtable-cell">
                                <div class="subtable-container">
                                    <ng-container *ngIf="subtableTemplate">
                                        <ng-container
                                            *ngTemplateOutlet="
                                                subtableTemplate;
                                                context: {
                                                    $implicit: row,
                                                    data: getSubtableData(row)
                                                }
                                            "
                                        ></ng-container>
                                    </ng-container>
                                </div>
                            </td>
                        </tr>
                    </ng-container>
                </tbody>
            </table>
        </div>
        <div *ngIf="pagination" class="pagination-wrapper">
            <ham-pagination
                [currentPage]="currentPage"
                [pageSize]="pageSize"
                [totalItems]="dataSource.length"
                (pageChange)="onPageChange($event)"
            ></ham-pagination>
        </div>
    `,
    styleUrls: ['./ham-table-with-actions.component.scss'],
})
export class HamTableWithActionsComponent implements AfterContentInit, OnChanges {
    private _dataSource: any[] = [];
    public checkboxSelection = new SelectionModel<any>(true, []);

    menuPosition = { top: 0, right: 0 };
    isMenuOpenIndex: number | null = null;
    allRowsChecked = false;
    allExpanded = false;

    @Input() columnClasses: string[] = [];
    @Input() tableStyles: string = '';
    @Input() menuItems: IAumMenuItem[] = [];
    @Input() rowMenuItems: IAumMenuItem[] = [];
    @Input() hideActions: boolean = false;
    /** Optional `data-testid` of every main row (e2e selector contracts); none by default. */
    @Input() rowTestId: string | null = null;
    @Input() showActionsInRowsOnly = false;
    @Input() clearSelections: boolean = false;

    // New subtable inputs
    @Input() hasSubtable: boolean = false;
    @Input() subtableDataProperty: string | null = null;
    @Input() subtableTemplate: TemplateRef<any> | null = null;
    @Input() expandedRows: Set<string> = new Set<string>();
    @Output() expandedRowsChange = new EventEmitter<Set<string>>();
    @Input() rowIdentifier: string;

    @ContentChild(HamSubtableDirective, { read: TemplateRef })
    _subtableTemplate: TemplateRef<any> | null = null;

    @Input()
    set dataSource(value: any[]) {
        if (!value) return;
        this._dataSource = value;
        this.totalPages = Math.ceil(value.length / this.pageSize);
        this.updatePaginatedData();
    }
    get dataSource() {
        return this._dataSource;
    }

    @Input() pageSize: number = 5;
    @Input() pagination: boolean = true;

    @Output() scrollEndReached = new EventEmitter<void>();
    @Output() rowSelected = new EventEmitter<any>();
    @Output() menuOptionClicked = new EventEmitter<any>();
    @Output() checkBoxSelectionChanged = new EventEmitter<any[]>();
    @Output() rowExpanded = new EventEmitter<{ row: any; expanded: boolean }>();
    @Output() subtableDataRequested = new EventEmitter<any>(); // For lazy loading

    @ContentChildren(HamHeaderCellDirective, { read: TemplateRef }) headerTemplates!: QueryList<TemplateRef<any>>;
    @ContentChildren(HamCellDirective) cellDirectives!: QueryList<HamCellDirective>;
    @ContentChildren(HamMenuButtonDirective, { read: TemplateRef }) menuButtons!: QueryList<TemplateRef<any>>;

    currentPage: number = 1;
    totalPages: number = 1;
    paginatedData: any[] = [];
    selectedRow: any = null;

    ngAfterContentInit() {
        this.totalPages = Math.ceil(this.dataSource.length / this.pageSize);
        this.updatePaginatedData();
    }

    ngOnChanges(changes: SimpleChanges) {
        // To set all selections to false from a parent (e.g., clear all selections button)
        if (changes['clearSelections'] && changes['clearSelections'].currentValue === true) {
            this.clearAllSelections();
        }
    }

    // Existing methods remain the same...
    onPageChange(page: number) {
        this.currentPage = page;
        this.updatePaginatedData();
    }

    onCellClicked(directive: HamCellDirective, row: any, event: MouseEvent) {
        directive.cellClick.emit(row);
    }

    selectRow(row: any) {
        this.selectedRow = row;
        this.rowSelected.emit(row);
    }

    rowChecked(row: any): void {
        this.checkboxSelection.toggle(row);
        this.allRowsChecked = this.isAllCheckboxesSelected();
        this.checkBoxSelectionChanged.emit(this.checkboxSelection.selected);
    }

    updatePaginatedData() {
        this.paginatedData = this.pagination
            ? this.dataSource.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize)
            : this.dataSource;
    }

    toggleOptionsMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.isMenuOpenIndex = 1;
        const buttonElement = event.target as HTMLElement;
        const rect = buttonElement.getBoundingClientRect();
        const right = window.innerWidth - rect.right;
        const top = rect.bottom;
        this.menuPosition = { top: top, right: right };
    }

    closeOptionsMenu(): void {
        this.isMenuOpenIndex = null;
    }

    toggleCheckAll() {
        this.allRowsChecked = this.isAllCheckboxesSelected();
        if (this.allRowsChecked) {
            this.checkboxSelection.clear();
            this.allRowsChecked = false;
        } else {
            this.allRowsChecked = true;
            this.dataSource.forEach((row) => {
                this.checkboxSelection.select(row);
            });
        }
        this.checkBoxSelectionChanged.emit(this.checkboxSelection.selected);
    }

    clearAllSelections() {
        this.checkboxSelection.clear();
        this.allRowsChecked = false;
        this.checkBoxSelectionChanged.emit(this.checkboxSelection.selected);
    }

    isAllCheckboxesSelected(): boolean {
        const numSelected = this.checkboxSelection.selected.length;
        const numRows = this.dataSource.length;
        return numSelected === numRows;
    }

    onMenuOptionClicked(option: IAumMenuItem): void {
        this.menuOptionClicked.emit(option.action);
        this.isMenuOpenIndex = null;
    }

    onScroll(event: Event): void {
        if (this.pagination) return;
        const element = event.target as HTMLElement;
        const threshold = 100;
        const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + threshold;
        if (atBottom) {
            this.scrollEndReached.emit();
        }
    }

    toggleExpandAll(): void {
        this.allExpanded = !this.allExpanded;

        if (this.allExpanded) {
            this.paginatedData.forEach((row) => {
                const rowId = row[this.rowIdentifier];
                this.expandedRows.add(rowId);
                this.rowExpanded.emit({ row, expanded: true });

                // Request data for lazy loading
                if (!this.getSubtableData(row) || this.getSubtableData(row).length === 0) {
                    this.subtableDataRequested.emit(row);
                }
            });
        } else {
            this.expandedRows.clear();
            this.paginatedData.forEach((row) => {
                this.rowExpanded.emit({ row, expanded: false });
            });
        }
        this.expandedRowsChange.emit(this.expandedRows);
    }

    getSubtableData(row: any): any[] {
        return row[this.subtableDataProperty] || [];
    }

    getTotalColumnCount(): number {
        let count = this.headerTemplates.length;
        if (this.hasSubtable) count++; // Expand column
        if (!this.hideActions) count++; // Checkbox column
        if (!this.hideActions || this.showActionsInRowsOnly) count++; // Actions column
        return count;
    }

    isSubtableRow = (index: number, item: any) => item.isSubtable;

    toggleSubtable(row: any, event: MouseEvent): void {
        event.stopPropagation();
        const rowId = row[this.rowIdentifier];
        if (this.expandedRows.has(rowId)) {
            this.expandedRows.delete(rowId);
        } else {
            this.expandedRows.add(rowId);
        }
        this.expandedRowsChange.emit(this.expandedRows);
    }

    isRowExpanded(row: any): boolean {
        const rowId = row[this.rowIdentifier];
        return this.expandedRows.has(rowId);
    }
}
